import { type RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  TEXT_ON_PATH_DOT_SIZE_PX,
  clampStartDistance,
  textOnPathHandleDistance,
} from '@/lib/shapes/text-on-path-session'
import type { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'
import { forwardDistanceFor, resolveTextOnPathDirection } from '@/lib/shapes/text-on-path-direction'
import type { TextOnPathDirection } from '../helpers/text-on-path-projection'

export interface TextOnPathPreview {
  /** Attach to the FORWARD outline path in `<defs>`; backs length and point-at-length queries. */
  pathRef: RefObject<SVGPathElement | null>
  /** Measured outline length; 0 without SVG geometry (jsdom) or without text on a path. */
  pathLength: number
  direction: TextOnPathDirection
  /** Start-dot centre in the shell's SVG space; null outside edit mode. */
  startDot: { x: number; y: number } | null
  /** Start-dot radius in path units, counter-scaled so it holds its screen size at any zoom. */
  startDotRadius: number
}

interface TextOnPathPreviewOptions {
  shapeId: string
  shapePath: string
  textOnPath: TextOnPathConfig | null | undefined
  isEditing: boolean
  canvasZoom: number | undefined
  onPathLengthChange?: (shapeId: string, pathLength: number) => void
}

/**
 * Text on a Path preview (WMPCM-9514). Measured off the same `shapePath` the shape itself draws,
 * so the preview can never disagree with the outline — no second path-resolution ladder, no
 * canvas→screen projection.
 */
export function useTextOnPathPreview({
  shapeId,
  shapePath,
  textOnPath,
  isEditing,
  canvasZoom,
  onPathLengthChange,
}: TextOnPathPreviewOptions): TextOnPathPreview {
  const pathRef = useRef<SVGPathElement>(null)
  const [pathLength, setPathLength] = useState(0)
  const [startDot, setStartDot] = useState<{ x: number; y: number } | null>(null)
  const startDistance = clampStartDistance(textOnPath?.startDistance ?? 0, pathLength)

  /**
   * Direction (WMPCM-9530, Story 3).
   *
   * `<textPath>` always runs text in its path's own direction, so a flip is a property of the
   * GEOMETRY, not of the text — hence a second, derived copy of the path in `<defs>`. SVG 2's
   * `side="right"` would be the spec answer and Chrome ignores it outright; see
   * `reverse-svg-path.ts` for the measurement.
   *
   * The perpendicular Offset rides in that same derived path (WMPCM-10320 Bug 3): displacing each
   * glyph instead piles them up inside every concave corner, which is what made a star unreadable.
   * See `offset-svg-path.ts` for the measurement there.
   *
   * Closed and open outlines flip differently, and `resolveTextOnPathDirection` owns that rule —
   * see it for why the Figma reference art appears to disagree with itself.
   *
   * `pathRef` deliberately stays on the FORWARD path either way: it backs
   * `getTotalLength`/`getPointAtLength` for the start dot, which marks a position on the outline
   * and must not move when the direction flips.
   */
  // Primitives, not the config object: the draft is a fresh object on every keystroke, and
  // re-parsing the path on each one would be wasted work.
  const clockwise = textOnPath?.clockwise ?? true
  const configuredOffset = textOnPath?.offset ?? 0
  const direction = useMemo(
    () =>
      resolveTextOnPathDirection({
        d: shapePath,
        clockwise,
        startDistance,
        pathLength,
        offset: configuredOffset,
      }),
    [shapePath, clockwise, startDistance, pathLength, configuredOffset]
  )

  // The dot is chrome: it must hold its screen size, and this SVG is inside the canvas zoom
  // transform, so counter-scale the radius rather than relying on a stroke-only trick.
  const startDotRadius = TEXT_ON_PATH_DOT_SIZE_PX / 2 / Math.max(canvasZoom ?? 1, 0.01)

  useLayoutEffect(() => {
    const el = pathRef.current

    // `getTotalLength`/`getPointAtLength` are unimplemented in jsdom, so every consumer of this
    // effect has to survive their absence rather than assume an SVG geometry engine.
    if (textOnPath == null || el == null || typeof el.getTotalLength !== 'function') {
      setPathLength(0)
      setStartDot(null)
      return
    }

    // The length is measured whether or not the bar is open (WMPCM-11097): the start distance is
    // clamped into `[0, length]`, so gating this on edit mode collapsed every committed start
    // position to 0 the moment the bar closed — the text jumped back to the path's origin even
    // though the stored (and saved) `startDistance` was correct. Only the dot is edit chrome.
    const total = el.getTotalLength()
    const length = Number.isFinite(total) && total > 0 ? total : 0

    setPathLength(length)

    if (!isEditing || length === 0 || typeof el.getPointAtLength !== 'function') {
      setStartDot(null)
      return
    }

    // The dot marks where the text BEGINS, which on a reversed open path is the opposite end of
    // the outline from `startDistance` (WMPCM-9530). Without this the dot sat at the far end from
    // the text it is supposed to anchor.
    const textStartForward = forwardDistanceFor(
      direction,
      clampStartDistance(textOnPath.startDistance, length),
      length
    )
    // ...and it sits a gap SHORT of that point rather than on it, or the disc covers the first
    // glyph (WMPCM-10320 Bug 2). The gap is the dot's own radius in path units, so its trailing
    // edge just meets the text at any zoom.
    const point = el.getPointAtLength(
      textOnPathHandleDistance({
        textStartDistance: textStartForward,
        pathLength: length,
        gap: startDotRadius,
        // A reversed open path reads against the path's direction, so "before" is the other way.
        runsForward: !direction.mirrored,
      })
    )

    setStartDot({ x: point.x, y: point.y })
    // `startDotRadius` is a real dependency: the gap is screen-sized, so the dot's path position
    // has to be recomputed when the canvas zoom changes.
  }, [isEditing, textOnPath, shapePath, direction, startDotRadius])

  // Separate from the measuring effect above: reporting upward re-renders the action bar, and
  // folding it in would make every start-position drag re-publish an unchanged path length.
  const lastReportedLengthRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isEditing || onPathLengthChange == null) {
      lastReportedLengthRef.current = null
      return
    }

    if (lastReportedLengthRef.current === pathLength) {
      return
    }

    lastReportedLengthRef.current = pathLength
    onPathLengthChange(shapeId, pathLength)
  }, [isEditing, pathLength, onPathLengthChange, shapeId])

  return { pathRef, pathLength, direction, startDot, startDotRadius }
}
